import React from "react";
import {
  DataLoadingWrapper,
  EmptyListContent,
  MultiSectionLayout,
  HeaderBreadcrumbs,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useStopSessionRecording } from "@web-src/modules/common/utils/posthogSetup";
import { ScrollablePage } from "@web-src/modules/common/components/ScrollablePage";
import {
  WhatsappAccountProvider,
  InstagramAccountProvider,
  EmailAccountProvider,
  TwilioAccountProvider,
  ClientSupportPageProvider,
} from "./providers";
import { useWhatsappAccount } from "./providers/WhatsappAccountProvider/useWhatsappAccount";
import { useInstagramAccount } from "./providers/InstagramAccountProvider/useInstagramAccount";
import { ConversationListSidebar } from "./components/ConversationListSidebar";
import { InboxSidebar } from "./components/InboxSidebar";
import { ClientSupportChat } from "./components/ClientSupportChat";
import { TicketsView } from "./components/TicketsView";
import { CreateTicketSidebar } from "./components/CreateTicketSidebar";
import { TicketDetailsSidebar } from "./components/TicketDetailsSidebar";
import { ReactComponent as PlatformsIcon } from "./assets/platforms.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ManageAccountsDrawer } from "./components/ManageAccountsDrawer";
import { WebChatBotAccountProvider } from "./providers/WebChatBotAccountProvider";
import { useWebChatBotAccount } from "./providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useEmailAccount } from "./providers/EmailAccountProvider/useEmailAccount";
import { ManageAccountsDrawerProvider } from "./components/ManageAccountsDrawer/providers/ManageAccountsDrawerProvider";
import { useManageAccountsDrawer } from "./components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useClientSupportPageContext } from "./providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useIsInboxSinglePane } from "./hooks/useIsInboxSinglePane";

export const InnerClientSupportPage: React.FC = () => {
  useStopSessionRecording();

  const {
    selectedView,
    ticketDetailsSidebarState,
    createTicketSidebarState,
    closeTicketDetailsSidebar,
    closeCreateTicketSidebar,
  } = useClientSupportPageContext();

  const {
    crmConfig,
    isManageAccountDrawerOpen,
    openManageAccountDrawer,
    isCRMConfigError,
    isCRMConfigLoading,
    refetchCRMConfig,
  } = useManageAccountsDrawer();

  const { whatsappAgent } = useWhatsappAccount();

  const { instagramAccount, isConnectingInstagramAccount } =
    useInstagramAccount();

  const { webChatBotAccount, isConnectingWebChatBotAccount } =
    useWebChatBotAccount();

  const { isConnected: isEmailConnected } = useEmailAccount();

  const isInboxSinglePane = useIsInboxSinglePane();

  const hasNoConnectedAccounts =
    !whatsappAgent &&
    !instagramAccount &&
    !webChatBotAccount &&
    !crmConfig?.twilio?.id &&
    !isEmailConnected;

  // Only the single-pane channel layout brings its own narrow-viewport
  // handling - every other state keeps the desktop width and scrolls instead
  const isPageScrollDisabled = isInboxSinglePane && !hasNoConnectedAccounts;

  const { t } = useTranslations();

  return (
    <>
      <DataLoadingWrapper
        isLoading={
          isConnectingInstagramAccount ||
          isConnectingWebChatBotAccount ||
          isCRMConfigLoading
        }
        isError={isCRMConfigError}
        onRetry={refetchCRMConfig}
      >
        <ScrollablePage isDisabled={isPageScrollDisabled}>
          {(() => {
            if (hasNoConnectedAccounts) {
              return (
                <MultiSectionLayout
                  header={
                    <HeaderBreadcrumbs
                      items={[
                        {
                          title: t({
                            id: "client-support-page.crm-settings",
                            defaultMessage: "CRM Settings",
                          }),
                        },
                      ]}
                    />
                  }
                >
                  <EmptyListContent
                    type="custom"
                    className="w-[460px]"
                    customImg={<PlatformsIcon />}
                    customTitle={t({
                      id: "client-support-page.welcome-to-crm-inbox",
                      defaultMessage: "Welcome to CRM Inbox",
                    })}
                    customSubtitle={t({
                      id: "client-support-page.connect-social-media-accounts",
                      defaultMessage:
                        "Connect your social media Accounts to receive and automate the messages from your Clients  🚀",
                    })}
                    customButton={{
                      text: t({
                        id: "client-support-page.add-new-account",
                        defaultMessage: "Add New Account",
                      }),
                      onClick: () =>
                        openManageAccountDrawer({
                          name: "connectedAccountList",
                        }),
                      iconEnd: <AddIcon />,
                    }}
                  />
                </MultiSectionLayout>
              );
            }

            const isChannelView = selectedView.kind === "channel";

            return (
              <MultiSectionLayout
                className="h-full w-full animate-fade-in"
                leftSide={<InboxSidebar />}
                classNames={
                  isChannelView
                    ? { middle: "flex-1 overflow-visible max-lg:min-w-0" }
                    : undefined
                }
              >
                {isChannelView ? (
                  <div className="flex h-full w-full min-w-0 flex-1 max-lg:relative">
                    <ConversationListSidebar />
                    <ClientSupportChat />
                  </div>
                ) : (
                  <TicketsView />
                )}
              </MultiSectionLayout>
            );
          })()}
        </ScrollablePage>
      </DataLoadingWrapper>
      <ManageAccountsDrawer
        isOpen={!!isManageAccountDrawerOpen}
        onRequestClose={() => openManageAccountDrawer(undefined)}
        initialScreen={isManageAccountDrawerOpen}
      />
      <TicketDetailsSidebar
        {...ticketDetailsSidebarState}
        onClose={closeTicketDetailsSidebar}
      />
      <CreateTicketSidebar
        {...createTicketSidebarState}
        onClose={closeCreateTicketSidebar}
      />
    </>
  );
};

export const ClientSupportPage: React.FC = () => {
  return (
    <ManageAccountsDrawerProvider isInboxModule>
      <WhatsappAccountProvider>
        <InstagramAccountProvider>
          <WebChatBotAccountProvider>
            <EmailAccountProvider>
              <TwilioAccountProvider>
                <ClientSupportPageProvider>
                  <InnerClientSupportPage />
                </ClientSupportPageProvider>
              </TwilioAccountProvider>
            </EmailAccountProvider>
          </WebChatBotAccountProvider>
        </InstagramAccountProvider>
      </WhatsappAccountProvider>
    </ManageAccountsDrawerProvider>
  );
};
